/**
 * Content-domain shapes as the admin UI consumes them. Deliberately plain
 * interfaces: this package reaches platform only through the admin server, so
 * it must not depend on `@pollyai/*` types. Field names mirror the platform
 * ContentSchema so the proxy can forward payloads untouched.
 */

export type ContentDifficulty = "easy" | "medium" | "hard";
export type ContentQuestionState = "active" | "draft" | "deleted";
export type ContentReviewStatus = "pending" | "approved" | "rejected";
export type ContentPlatform = "slack" | "teams" | "events";
export type ContentFlagReason =
  | "inappropriate"
  | "wrong_answer"
  | "too_hard"
  | "other";
export type ContentFlagStatus = "open" | "resolved";
export type ContentFlagResolutionAction =
  | "removed"
  | "edited"
  | "hidden"
  | "dismissed"
  | "restored";
export type ContentEvalVote = "up" | "down";

/** QA rubric codes. C7/C11/C13 are hard failures; C2/C4/C12 are warnings. */
export type ContentQaCheckCode = "C2" | "C4" | "C7" | "C11" | "C12" | "C13";

export const QA_CHECK_LABELS: Record<ContentQaCheckCode, string> = {
  C2: "Choice label over the platform cap",
  C4: "Duplicate choice labels",
  C7: "Needs exactly 4 choices",
  C11: "Needs exactly one correct choice",
  C12: "Duplicate of an existing question",
  C13: "Profanity detected",
};

export interface ContentChoice {
  id: string;
  label: string;
  correct: boolean;
}

export interface ContentQa {
  passedAll: boolean;
  checks: { code: ContentQaCheckCode; pass: boolean }[];
}

export interface ContentSourceInfo {
  kind: string;
  legacyId?: string | null;
  note?: string | null;
  model?: string | null;
}

export interface ContentQuestionRow {
  id: string;
  contentType: string;
  title: string;
  categoryId: string;
  difficulty: ContentDifficulty;
  state: ContentQuestionState;
  reviewStatus?: ContentReviewStatus | null;
  hiddenOnPlatforms: ContentPlatform[];
  batchId?: string | null;
  choices: ContentChoice[];
  qa?: ContentQa | null;
  source: ContentSourceInfo;
  createdBy: string;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt: string;
}

export interface ContentCategoryRow {
  id: string;
  contentType: string;
  group: string;
  name: string;
  emoji?: string | null;
  favorite: boolean;
  favoriteRank?: number | null;
  hidden: boolean;
  hiddenOnPlatforms: ContentPlatform[];
  state: string;
  questionCount: number;
  updatedAt: string;
}

/** Groups of category names that normalize to the same string — merge candidates. */
export interface ContentNearVariantGroup {
  normalized: string;
  names: string[];
}

export interface ContentCategoriesResult {
  categories: ContentCategoryRow[];
  nearVariants: ContentNearVariantGroup[];
}

export interface ContentBatchRow {
  id: string;
  name: string;
  contentType: string;
  state: string;
  hiddenOnPlatforms: ContentPlatform[];
  questionIds: string[];
  createdBy: string;
  createdAt: string;
  publishedAt?: string | null;
  reviewedCount: number;
  approvedCount: number;
  rejectedCount: number;
  generation?: ContentBatchGeneration | null;
}

/** Whole percentages per difficulty, summing to 100. */
export interface ContentDifficultyMix {
  easy: number;
  medium: number;
  hard: number;
}

export interface ContentBatchGeneration {
  status: string;
  prompt: string;
  requested: number;
  produced: number;
  qaPassed: number;
  /**
   * Heartbeat, bumped by every chunk write. A `running` batch whose heartbeat
   * has gone quiet is stalled, not running — see `effectiveGenerationStatus`.
   */
  updatedAt: string;
  categoryIds?: string[] | null;
  difficultyMix?: ContentDifficultyMix | null;
  model?: string | null;
  error?: string | null;
}

export interface ContentFlagRow {
  id: string;
  questionId?: string | null;
  questionTitle?: string | null;
  reason: ContentFlagReason;
  comment?: string | null;
  customer?: string | null;
  flaggedBy: string;
  flaggedAt: string;
  status: ContentFlagStatus;
  resolution?: {
    action: ContentFlagResolutionAction;
    by: string;
    at: string;
    note?: string | null;
  } | null;
}

export interface ContentEvalRow {
  id: string;
  questionId: string;
  vote: ContentEvalVote;
  rating?: number | null;
  comment?: string | null;
  votedBy: string;
  context: string;
  updatedAt: string;
}

/** One row of the lint report; `detail` names the specific violation. */
export interface ContentLintRow {
  questionId: string;
  title: string;
  categoryId: string;
  difficulty: ContentDifficulty;
  detail?: string | null;
  copies?: number | null;
}

export interface ContentLintReport {
  overLengthLabels: ContentLintRow[];
  duplicateChoices: ContentLintRow[];
  duplicateTitles: ContentLintRow[];
}
